import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Link2, Unlink, AlertTriangle, History, Layers, Boxes } from "lucide-react";
import { toast } from "sonner";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle } from "@/components/ops-ui";

const PLATFORMS = [["", "All platforms"], ["shopee", "Shopee"], ["tiktok", "TikTok Shop"]];
const STATES = [
  ["", "All"], ["conflict", "Conflict"], ["unmapped", "Unmapped"], ["invalid", "Invalid"], ["mapped", "Mapped"],
];
const STATE_STYLE = {
  mapped: "border-emerald-300 text-emerald-700 bg-emerald-50",
  unmapped: "border-slate-300 text-slate-600 bg-slate-50",
  conflict: "border-amber-300 text-amber-800 bg-amber-50",
  invalid: "border-rose-300 text-rose-700 bg-rose-50",
};

export default function MarketplaceSkuMapping() {
  const [sp, setSp] = useSearchParams();
  const [platform, setPlatform] = useState(sp.get("platform") || "");
  const [shopId, setShopId] = useState(sp.get("shop_id") || "");
  const [state, setState] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapRow, setMapRow] = useState(null);
  const [auditRow, setAuditRow] = useState(null);
  const { density, setDensity } = useUiPrefs("mp-mapping");

  const load = useCallback(async (p = platform, s = shopId, st = state) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ platform: p, shop_id: s, state: st }).toString();
      const r = await api.get(`/marketplace/mappings/workspace?${params}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [platform, shopId, state]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stores = (data?.stores || []).filter((s) => !platform || s.platform === platform);
  const rows = data?.items || [];
  const counts = data?.counts || {};

  const onPlatform = (p) => { setPlatform(p); setShopId(""); setSp(p ? { platform: p } : {}); load(p, "", state); };
  const onShop = (s) => { setShopId(s); const q = { ...(platform ? { platform } : {}), ...(s ? { shop_id: s } : {}) }; setSp(q); load(platform, s, state); };
  const onState = (st) => { setState(st); load(platform, shopId, st); };

  const afterSave = () => { setMapRow(null); load(); };

  return (
    <div className="space-y-4" data-testid="marketplace-mapping-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">Marketplace</div>
          <h1 className="text-2xl font-bold">SKU Mapping</h1>
          <p className="text-sm text-slate-500 mt-0.5">Link each shop's marketplace SKU to a Master SKU or Combo. Strictly per store — never guessed.</p>
        </div>
        <DensityToggle density={density} setDensity={setDensity} testid="map-density-toggle" />
      </div>

      {/* filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Platform</label>
          <select data-testid="map-platform" value={platform} onChange={(e) => onPlatform(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
            {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Store</label>
          <select data-testid="map-store" value={shopId} onChange={(e) => onShop(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
            <option value="">All connected stores{platform ? "" : " (choose platform)"}</option>
            {stores.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* state tabs + counts */}
      <div className="flex flex-wrap gap-1.5" data-testid="map-state-tabs">
        {STATES.map(([v, l]) => (
          <button key={v || "all"} data-testid={`map-state-${v || "all"}`} onClick={() => onState(v)}
            className={`px-3 h-8 rounded-md text-xs border flex items-center gap-1.5 ${state === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>
            {l}
            {v && <span className={`px-1.5 rounded-full text-[10px] ${state === v ? "bg-white/20" : "bg-slate-100"}`}>{counts[v] ?? 0}</span>}
            {!v && <span className={`px-1.5 rounded-full text-[10px] ${state === v ? "bg-white/20" : "bg-slate-100"}`}>{data?.total ?? 0}</span>}
          </button>
        ))}
      </div>

      {/* table */}
      {loading ? <div className="p-8 text-center text-slate-400">Loading…</div> : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className={`w-full text-sm min-w-[760px] ${density === "compact" ? "[&_td]:py-1 [&_th]:py-1" : ""}`} data-testid="map-table">
            <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
              <th className="p-2 text-left">Marketplace SKU</th>
              <th className="p-2 text-left">Store</th>
              <th className="p-2 text-left">Orders / Qty</th>
              <th className="p-2 text-left">State</th>
              <th className="p-2 text-left">Mapped Target</th>
              <th className="p-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400" data-testid="map-empty">No SKUs to map for this filter</td></tr> :
                rows.map((it) => {
                  const key = `${it.platform}-${it.shop_id}-${it.marketplace_sku}`;
                  return (
                    <tr key={key} data-testid={`map-row-${it.marketplace_sku}`} className="border-t border-slate-100 align-top">
                      <td className="p-2"><div className="font-mono text-xs font-semibold">{it.marketplace_sku}</div><div className="text-[10px] text-slate-400 capitalize">{it.platform}{it.name ? ` · ${it.name}` : ""}</div></td>
                      <td className="p-2 text-xs text-slate-500">{it.store}</td>
                      <td className="p-2 text-xs text-slate-500">{it.order_count} / {it.total_qty}</td>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[10px] ${STATE_STYLE[it.state]}`} data-testid={`map-state-badge-${it.marketplace_sku}`}>{it.state}</Badge>
                        {it.state === "conflict" && <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />matches Master & Combo — choose one</div>}
                        {it.state === "invalid" && <div className="text-[10px] text-rose-600 mt-1">{it.reason === "target_inactive" ? "target inactive" : "target missing"}</div>}
                        {it.state === "unmapped" && it.candidate && <div className="text-[10px] text-slate-400 mt-1">candidate: {it.candidate.type} {it.candidate.code}</div>}
                      </td>
                      <td className="p-2 text-xs">
                        {it.state === "mapped" ? (
                          <div className="flex items-center gap-1.5">
                            {it.target_type === "combo" ? <Boxes className="w-3.5 h-3.5 text-indigo-500" /> : <Layers className="w-3.5 h-3.5 text-emerald-500" />}
                            <span className="font-mono">{it.target_code}</span>
                            {it.target_type === "combo" && <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700">combo · routing review</Badge>}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 text-xs mr-1" data-testid={`map-btn-${it.marketplace_sku}`}
                          onClick={() => setMapRow(it)}><Link2 className="w-3 h-3 mr-1" />{it.state === "mapped" ? "Remap" : "Map"}</Button>
                        {it.mapping_id && <Button size="sm" variant="outline" className="h-7 text-xs mr-1 text-rose-600 border-rose-200" data-testid={`unmap-btn-${it.marketplace_sku}`}
                          onClick={() => doUnmap(it, load)}><Unlink className="w-3 h-3" /></Button>}
                        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`audit-btn-${it.marketplace_sku}`}
                          onClick={() => setAuditRow(it)}><History className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      <MapDialog row={mapRow} onClose={() => setMapRow(null)} onSaved={afterSave} />
      <AuditDialog row={auditRow} onClose={() => setAuditRow(null)} />
    </div>
  );
}

async function doUnmap(it, reload) {
  if (!window.confirm(`Unmap ${it.marketplace_sku} from ${it.target_code}? This is audited.`)) return;
  try {
    await api.delete(`/marketplace/mappings/${it.mapping_id}`);
    toast.success("Unmapped (audit preserved)");
    reload();
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
}

function MapDialog({ row, onClose, onSaved }) {
  const [ttype, setTtype] = useState("master");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      const def = row.candidate?.type === "combo" ? "combo" : "master";
      setTtype(def); setQ(""); setResults([]); setSelected(null);
    }
  }, [row]);

  if (!row) return null;

  const search = async () => {
    try {
      const r = await api.get(`/marketplace/mappings/search-targets?type=${ttype}&q=${encodeURIComponent(q)}`);
      setResults(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!selected) return toast.error("Select a target first");
    setSaving(true);
    try {
      await api.post("/marketplace/mappings", {
        platform: row.platform, shop_id: row.shop_id, marketplace_sku: row.marketplace_sku,
        target_type: ttype, target_id: selected.id,
      });
      toast.success(`Mapped ${row.marketplace_sku} → ${selected.code}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="map-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" />Map SKU</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-slate-50 rounded-md p-2.5">
            <span className="font-mono font-semibold">{row.marketplace_sku}</span>
            <span className="text-slate-400"> · {row.store} · {row.platform}</span>
          </div>
          {row.state === "conflict" && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />This SKU text matches both a Master SKU and a Combo. Choose explicitly — nothing is auto-selected.
            </div>
          )}
          <div className="flex gap-1.5">
            {[["master", "Master SKU", Layers], ["combo", "Combo", Boxes]].map(([v, l, Icon]) => (
              <button key={v} data-testid={`map-ttype-${v}`} onClick={() => { setTtype(v); setResults([]); setSelected(null); }}
                className={`px-3 h-8 rounded-md text-xs border flex items-center gap-1.5 ${ttype === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>
                <Icon className="w-3.5 h-3.5" />{l}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="map-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder={ttype === "combo" ? "Search combo code / name" : "Search Master SKU / name"} className="pl-8 h-9" /></div>
            <Button data-testid="map-search-btn" onClick={search} className="h-9 bg-slate-900 hover:bg-slate-800"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-52 overflow-y-auto rounded-md border border-slate-200 divide-y" data-testid="map-results">
            {results.length === 0 ? <div className="p-3 text-xs text-slate-400">Type and press Search to find a target</div> :
              results.map((r) => (
                <button key={r.id} data-testid={`map-result-${r.code}`} onClick={() => setSelected(r)}
                  className={`w-full text-left p-2 text-xs flex items-center gap-2 ${selected?.id === r.id ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                  <span className="font-mono font-semibold">{r.code}</span>
                  <span className="text-slate-500 truncate">{r.name}</span>
                  {r.active === false && <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-600 ml-auto">inactive</Badge>}
                  {selected?.id === r.id && <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 ml-auto">selected</Badge>}
                </button>
              ))}
          </div>
          {ttype === "combo" && <div className="text-[11px] text-indigo-700">Combo is stored as a single relationship. No component expansion — the line is flagged for routing review.</div>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
            <Button data-testid="map-save-btn" onClick={save} disabled={!selected || saving} className="h-9 bg-emerald-600 hover:bg-emerald-700">{saving ? "Saving…" : "Save mapping"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ row, onClose }) {
  const [log, setLog] = useState(null);
  useEffect(() => {
    if (!row) { setLog(null); return; }
    (async () => {
      try {
        const params = new URLSearchParams({ platform: row.platform, shop_id: row.shop_id, marketplace_sku: row.marketplace_sku }).toString();
        const r = await api.get(`/marketplace/mappings/audit?${params}`);
        setLog(r.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [row]);

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="audit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" />Mapping History{row ? ` — ${row.marketplace_sku}` : ""}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {log == null ? <div className="p-3 text-xs text-slate-400">Loading…</div> :
            log.length === 0 ? <div className="p-3 text-xs text-slate-400">No history yet</div> :
              log.map((a) => (
                <div key={a.id} className="text-xs border border-slate-200 rounded-md p-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] capitalize">{a.action}</Badge>
                    <span className="text-slate-400">{a.at ? new Date(a.at).toLocaleString() : ""}</span>
                  </div>
                  <div className="mt-1 text-slate-600">
                    {a.detail?.target_type && <>→ <span className="font-mono">{a.detail.target_code}</span> <span className="text-slate-400">({a.detail.target_type})</span></>}
                    {a.detail?.from && <span className="text-slate-400"> · from {a.detail.from.target_code}</span>}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">by {a.by || "—"}</div>
                </div>
              ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
